/**
 * Pure "what happens next" derivation for a material line.
 *
 * Replaces the older `materialStatus`, which answered "is there enough?" — a
 * question the Sourcing screen then made the user translate into an action by
 * reading five more numbers. This answers the actual question ("what do I do
 * with this material?") so the UI can render one sentence and one button.
 *
 * It also fixes two defects in that predecessor:
 *   - it never read `issued`, so a fully-issued line read "sufficient" forever;
 *   - `received` and `onHand` were summed before any branching, so "I already
 *     have this in the store" and "it arrived on a PO" were indistinguishable.
 *
 * Kept DB-free so the whole table is unit-testable. All quantities are millis.
 */
import type { MaterialPlan } from "./types.js";

/** How much still has to reach cutting. */
export function stillNeededMilli(requiredMilli: number, issuedMilli: number): number {
  return Math.max(0, requiredMilli - issuedMilli);
}

/**
 * What can be issued right now: unissued goods-receipts plus warehouse stock.
 *
 * `issued` is subtracted from `received` even when the issue actually drew from
 * warehouse stock (the issue record doesn't say which). That under-counts
 * rather than over-promises — the safe direction, since over-promising would
 * tell a floor supervisor to cut with fabric that isn't there.
 */
export function availableMilli(
  receivedMilli: number,
  onHandMilli: number,
  issuedMilli: number,
): number {
  return Math.max(0, receivedMilli - issuedMilli) + onHandMilli;
}

/**
 * How much is genuinely still in transit — ordered but not yet received.
 *
 * `ordered` alone is not enough: a PO that has been fully received is done, and
 * a line resting on it should read "needs ordering", not "waiting on vendor".
 */
export function inboundMilli(orderedMilli: number, receivedMilli: number): number {
  return Math.max(0, orderedMilli - receivedMilli);
}

/** How much still has to be bought, after everything already available. */
export function toOrderMilli(
  requiredMilli: number,
  receivedMilli: number,
  onHandMilli: number,
  issuedMilli: number,
): number {
  const needed = stillNeededMilli(requiredMilli, issuedMilli);
  return Math.max(0, needed - availableMilli(receivedMilli, onHandMilli, issuedMilli));
}

/** Legacy shortfall — required minus everything in hand. Drives the PO default qty. */
export function shortByMilli(
  requiredMilli: number,
  receivedMilli: number,
  onHandMilli: number,
): number {
  return Math.max(0, requiredMilli - receivedMilli - onHandMilli);
}

export interface MaterialPlanInput {
  requiredMilli: number;
  receivedMilli: number;
  onHandMilli: number;
  orderedMilli: number;
  issuedMilli: number;
}

/**
 * A line is "sourced enough to sample" when the goods are on-site and
 * available (`ready`) or already drawn to cutting (`issued`). Shared by the
 * sampling sourcing-gate (guard + UI) — one predicate keeps the guard and the
 * badges from drifting. @see docs/adr/0047-block-sampling-until-sourced.md
 */
const SOURCED_PLANS: ReadonlySet<MaterialPlan> = new Set(["ready", "issued"]);
export function isSourcedForSampling(plan: MaterialPlan): boolean {
  return SOURCED_PLANS.has(plan);
}

/**
 * Short, human label for each `MaterialPlan` — the copy shown in badges and
 * on the sourcing-gate card. Exhaustive over `MaterialPlan` so consumers
 * never need a fallback.
 */
export const MATERIAL_PLAN_LABEL: Record<MaterialPlan, string> = {
  to_source: "to source",
  awaiting: "PO out, awaiting delivery",
  partly_ready: "partly received",
  ready: "ready",
  issued: "issued",
};

/**
 * The single next step for a line. Ordered most-settled first, so the first
 * matching branch is always the truest description of where the line stands.
 */
export function materialPlan(input: MaterialPlanInput): MaterialPlan {
  const needed = stillNeededMilli(input.requiredMilli, input.issuedMilli);
  if (needed === 0) return "issued";

  const available = availableMilli(input.receivedMilli, input.onHandMilli, input.issuedMilli);
  if (available >= needed) return "ready";
  if (available > 0) return "partly_ready";
  if (inboundMilli(input.orderedMilli, input.receivedMilli) > 0) return "awaiting";
  return "to_source";
}
